import { Alert, Box, Button, Card, Divider, Drawer, Group, SimpleGrid, Skeleton, Stack, Table, Text, ThemeIcon } from "@mantine/core";
import { IconFileOff, IconFileText, IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ChargeContractApi } from "../../../../apis";
import type { ChargeContract, ComponentInput } from "../../../../apis/chargeContract";
import { ComponentEditor } from "../../../../components/billing/ComponentEditor";
import { ConflictReloadModal } from "../../../../components/billing/ConflictReloadModal";
import { ContractComponentCard } from "../../../../components/billing/ContractComponentCard";
import { ContractHeaderActions } from "../../../../components/billing/ContractHeaderActions";
import { ContractStatusBadge } from "../../../../components/billing/ContractStatusBadge";
import { EmptyState } from "../../../../components/billing/EmptyState";
import { EndCustomerRollupPreview } from "../../../../components/billing/EndCustomerRollupPreview";
import { PermissionGate } from "../../../../components/billing/PermissionGate";
import { SectionHeader } from "../../../../components/billing/SectionHeader";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { useOptimisticMutation } from "../../../../hooks/useOptimisticMutation";
import { formatDate, formatMoney, formatPercent } from "../../../../utils/format";

type ContractComponent = ChargeContract["components"][number];

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

// Backend `update` (component edit) accepts all five component types on DRAFT
// contracts (END_CUSTOMER_RECURRING write support added in the billing service).
const EDITABLE_TYPES: ComponentInput["componentType"][] = [
  "RECURRING_SUBSCRIPTION",
  "TRANSACTION_FEE",
  "ONE_TIME_PURCHASE",
  "FREE_OR_CUSTOM",
  "END_CUSTOMER_RECURRING",
];


const COMPONENT_ORDER = [
  { type: "RECURRING_SUBSCRIPTION"  as ComponentInput["componentType"], label: "Сар бүрийн төлбөр" },
  { type: "TRANSACTION_FEE"         as ComponentInput["componentType"], label: "Гүйлгээний шимтгэл" },
  { type: "END_CUSTOMER_RECURRING"  as ComponentInput["componentType"], label: "End-customer сар бүрийн төлбөр" },
  { type: "ONE_TIME_PURCHASE"       as ComponentInput["componentType"], label: "Нэг удаагийн төлбөр" },
  { type: "FREE_OR_CUSTOM"          as ComponentInput["componentType"], label: "Үнэгүй / Захиалгат" },
] as const;

const FREQ_LABEL: Record<string, string> = {
  MONTHLY  : "сар",
  QUARTERLY: "улирал",
  ANNUAL   : "жил",
};

function toInput(c: ContractComponent): ComponentInput {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...rest } = c as ContractComponent & { id: string };
  return rest as ComponentInput;
}

function renderSummary(c: ContractComponent): React.ReactNode {
  switch (c.componentType) {
    case "RECURRING_SUBSCRIPTION":
      return (
        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatMoney(c.recurringAmount, c.currency)} / {FREQ_LABEL[c.recurringFrequency ?? ""] ?? c.recurringFrequency ?? "—"}
        </Text>
      );
    case "ONE_TIME_PURCHASE":
      return (
        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatMoney(c.oneTimeAmount, c.currency)}
          {c.oneTimeLicenseExpiresAt ? ` · ${formatDate(c.oneTimeLicenseExpiresAt)}-хүртэл` : ""}
        </Text>
      );
    case "TRANSACTION_FEE":
      return (
        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatPercent(Number(c.txFeeRate) * 100)}
          {c.txFeeMinAmount ? ` · мин ${formatMoney(c.txFeeMinAmount, c.currency)}` : ""}
          {c.txFeeMaxAmount ? ` · макс ${formatMoney(c.txFeeMaxAmount, c.currency)}` : ""}
        </Text>
      );
    case "FREE_OR_CUSTOM":
      return <Text size="sm">{c.customTerms ?? "—"}</Text>;
    case "END_CUSTOMER_RECURRING": {
      // TODO: wire endCustomerCount endpoint when available
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const count: number | undefined = (c as any).endCustomerCount;
      return (
        <Stack gap={4}>
          <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatMoney(c.ecRecurringAmount, c.currency)} / {FREQ_LABEL[c.ecRecurringFrequency ?? ""] ?? c.ecRecurringFrequency ?? "—"}
          </Text>
          {count != null && count > 0 && (
            <EndCustomerRollupPreview
              customerCount={count}
              perCustomerAmount={Number(c.ecRecurringAmount ?? 0)}
              currency={c.currency}
              frequency={(c.ecRecurringFrequency as "MONTHLY" | "QUARTERLY" | "ANNUAL") ?? "MONTHLY"}
            />
          )}
        </Stack>
      );
    }
    default:
      return null;
  }
}


function ActiveContractSection({
  contract,
  editable,
  onEdit,
  onRemove,
  onAdd,
  onToggle,
  onDetails,
}: {
  contract: ChargeContract;
  editable: boolean;
  onEdit: (c: ContractComponent) => void;
  onRemove: (c: ContractComponent) => void;
  onAdd: (t: ComponentInput["componentType"]) => void;
  onToggle: (c: ContractComponent, enabled: boolean) => void;
  onDetails: () => void;
}) {
  return (
    <Stack gap="sm">
      <Card withBorder padding="lg" radius="md" style={heroWash}>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="brand" size={44} radius="md">
                <IconFileText size={24} />
              </ThemeIcon>
              <Box>
                <Text fw={700} fz="xl" style={{ fontVariantNumeric: "tabular-nums" }}>{contract.contractNumber}</Text>
                <Text size="sm" c="dimmed">{contract.contractName}</Text>
              </Box>
            </Group>
            <ContractStatusBadge status={contract.status} />
          </Group>
          <Divider />
          <SimpleGrid cols={{ base: 3 }} spacing="md">
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Валют</Text>
              <Text fw={500}>{contract.currency}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Үйлчилгээ эхэлсэн</Text>
              <Text fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(contract.effectiveFrom)}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Үйлчилгээ дуусах</Text>
              <Text fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{contract.effectiveUntil ? formatDate(contract.effectiveUntil) : "—"}</Text>
            </Box>
          </SimpleGrid>
        </Stack>
      </Card>

      <SectionHeader eyebrow="Гэрээний нөхцөл" title={`Бүрэлдэхүүн (${contract.components.length})`} />

      {COMPONENT_ORDER.map(({ type, label }) => {
        const existing = contract.components.find((c) => c.componentType === type);
        const canEditThis = editable && EDITABLE_TYPES.includes(type);
        return (
          <ContractComponentCard
            key={type}
            type={type}
            label={label}
            isPresent={!!existing}
            isEnabled={existing?.isEnabled}
            summary={existing ? renderSummary(existing) : undefined}
            onAdd={canEditThis && !existing ? () => onAdd(type) : undefined}
            onEdit={canEditThis && existing ? () => onEdit(existing) : !canEditThis && existing ? () => onDetails() : undefined}
            onToggle={canEditThis && existing ? (v) => onToggle(existing, v) : undefined}
          />
        );
      })}
    </Stack>
  );
}

export function TenantContractTab({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const canCreate = useHasPermission("BIL_002");
  const { data, isLoading, mutate } = useSWR(
    `swr.billing.tenant.contracts.${tenantId}`,
    () => ChargeContractApi.list({ tenantId, page: 1, limit: 50 })
  );
  const { conflict, clearConflict, run } = useOptimisticMutation();
  const [editing, setEditing] = useState<{ value: ComponentInput; isNew: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: ChargeContract[] = (data as any)?.rows ?? (Array.isArray(data) ? (data as ChargeContract[]) : []);

  if (isLoading) {
    return <Skeleton height={220} radius="md" />;
  }

  if (rows.length === 0) {
    return (
      <Card withBorder radius="md" padding="md">
        <EmptyState
          icon={<IconFileOff size={26} />}
          message="Энэ тенантад гэрээ байхгүй байна."
          action={
            <PermissionGate code="BIL_002">
              <Button variant="light" color="brand" onClick={() => navigate(`/billing/contracts/new?tenantId=${tenantId}`)}>
                Шинэ гэрээ
              </Button>
            </PermissionGate>
          }
        />
      </Card>
    );
  }

  const displayContract = rows.find((c) => c.status === "ACTIVE") ?? rows[0] ?? null;
  // Component edit is backend-allowed only on DRAFT contracts (all five types
  // are editable). A non-DRAFT contract is read-only; change an active contract
  // by creating a new (superseding) one.
  const editable =
    !!displayContract &&
    displayContract.status === "DRAFT" &&
    displayContract.components.every((c) => EDITABLE_TYPES.includes(c.componentType));

  const persist = async (next: ComponentInput[], onOk?: () => void) => {
    if (!displayContract) return;
    setBusy(true);
    try {
      const res = await run(() =>
        ChargeContractApi.update(displayContract.id, {
          rowVersion: displayContract.rowVersion,
          contractName: displayContract.contractName,
          effectiveFrom: displayContract.effectiveFrom,
          components: next,
        })
      );
      if (res.ok) {
        await mutate();
        onOk?.();
      }
    } catch (e: unknown) {
      // eslint-disable-next-line no-alert
      alert(`Хадгалахад алдаа: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const saveComponent = async () => {
    if (!editing || !displayContract) return;
    const others = displayContract.components
      .map(toInput)
      .filter((c) => c.componentType !== editing.value.componentType);
    await persist([...others, editing.value], () => setEditing(null));
  };

  const removeComponent = async (comp: ContractComponent) => {
    if (!displayContract) return;
    const next = displayContract.components.map(toInput).filter((c) => c.componentType !== comp.componentType);
    if (next.length === 0) {
      // eslint-disable-next-line no-alert
      alert("Гэрээнд дор хаяж нэг бүрэлдэхүүн үлдэх ёстой.");
      return;
    }
    await persist(next);
  };

  const toggleComponent = async (comp: ContractComponent, enabled: boolean) => {
    if (!displayContract) return;
    const next = displayContract.components.map((c) =>
      c.componentType === comp.componentType ? { ...toInput(c), isEnabled: enabled } : toInput(c)
    );
    await persist(next);
  };

  const terminateContract = async () => {
    if (!displayContract) return;
    // eslint-disable-next-line no-alert
    const reason = prompt("Цуцлах шалтгаан:");
    if (reason == null) return;
    setBusy(true);
    try {
      await ChargeContractApi.terminate(displayContract.id, displayContract.rowVersion, reason);
      await mutate();
    } catch (e: unknown) {
      // eslint-disable-next-line no-alert
      alert(`Цуцлахад алдаа: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="xl">
      {/* ── Active / shown contract ── */}
      {displayContract && (
        <Stack gap="sm">
          <SectionHeader
            eyebrow="Платформ төлбөр"
            title={displayContract.status === "ACTIVE" ? "Идэвхтэй гэрээ" : "Сүүлийн гэрээ"}
            action={
              <ContractHeaderActions
                onTerminate={terminateContract}
                onClone={() => alert("Удахгүй")}
                onExportPdf={() => alert("Удахгүй")}
                onNewContract={() => navigate(`/billing/contracts/new?tenantId=${tenantId}`)}
                canTerminate={displayContract.status === "ACTIVE" || displayContract.status === "DRAFT"}
                canCreate={canCreate}
              />
            }
          />
          {displayContract.status !== "ACTIVE" && (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
              Одоогоор идэвхтэй гэрээ байхгүй. {displayContract.status === "DRAFT" ? "Ноорог" : "Хамгийн сүүлийн"} гэрээг харуулж байна.
            </Alert>
          )}
          <ActiveContractSection
            contract={displayContract}
            editable={editable}
            onEdit={(c) => setEditing({ value: toInput(c), isNew: false })}
            onRemove={removeComponent}
            onAdd={(t) => setEditing({ value: { componentType: t, currency: displayContract.currency }, isNew: true })}
            onToggle={toggleComponent}
            onDetails={() => navigate(`/billing/contracts/${displayContract.id}`)}
          />
        </Stack>
      )}

      {/* ── Contract history ── */}
      <Stack gap="sm">
        <SectionHeader eyebrow="Түүх" title="Гэрээний түүх" />
        <Card withBorder radius="md" padding={0}>
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Контракт №</Table.Th>
                  <Table.Th>Хугацаа</Table.Th>
                  <Table.Th>Бүрэлдэхүүн</Table.Th>
                  <Table.Th>Төлөв</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((c) => (
                  <Table.Tr
                    key={c.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/billing/contracts/${c.id}`)}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{c.contractNumber}</Text>
                      <Text size="xs" c="dimmed">{c.contractName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatDate(c.effectiveFrom)}
                        {" — "}
                        {c.effectiveUntil ? formatDate(c.effectiveUntil) : "…"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{c.components.length}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ContractStatusBadge status={c.status} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </Stack>

      <Drawer
        opened={!!editing}
        onClose={() => setEditing(null)}
        position="right"
        size="lg"
        title={editing?.isNew ? "Бүрэлдэхүүн нэмэх" : "Бүрэлдэхүүн засах"}
      >
        {editing && (
          <Stack gap="md">
            <ComponentEditor
              value={editing.value}
              onChange={(v) => setEditing({ ...editing, value: v })}
              currencyOptions={displayContract ? [displayContract.currency] : ["MNT"]}
              tenantId={tenantId}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)}>Болих</Button>
              <Button loading={busy} onClick={saveComponent}>Хадгалах</Button>
            </Group>
          </Stack>
        )}
      </Drawer>

      <ConflictReloadModal opened={conflict} onReload={() => { clearConflict(); mutate(); }} />
    </Stack>
  );
}
